import { useState } from 'react';
import {
  useAdminCreateStandingInvitationMutation,
  useAdminDeleteInvitationMutation,
  MembershipRole,
  AdminSearchOrganizationQuery,
} from 'src/generated/graphql';
import { formatDistanceToNow } from 'date-fns';

type Organization = NonNullable<AdminSearchOrganizationQuery['organization']>;
type StandingInvitation = Organization['standingInvitations'][number];

type InvitationSectionProps = {
  organization: Organization;
  onInvitationChange: () => void;
};

export const InvitationSection = ({
  organization,
  onInvitationChange,
}: InvitationSectionProps) => {
  const [generatedLink, setGeneratedLink] = useState<string | null>(null);
  const [showCopied, setShowCopied] = useState(false);
  const [selectedRole, setSelectedRole] = useState<MembershipRole>(MembershipRole.Admin);
  const [error, setError] = useState<string | null>(null);

  const [createInvitation, { loading: createLoading }] =
    useAdminCreateStandingInvitationMutation({
      onCompleted: (data) => {
        const invitation = data.adminCreateStandingInvitation;
        if (!invitation) return;
        const link = `${window.location.origin}/invite/${invitation.code}`;
        setGeneratedLink(link);
        setError(null);
        onInvitationChange();
      },
      onError: (err) => {
        setError(err.message);
        setGeneratedLink(null);
      },
    });

  const [deleteInvitation, { loading: deleteLoading }] =
    useAdminDeleteInvitationMutation({
      onCompleted: () => {
        onInvitationChange();
      },
    });

  const handleGenerate = () => {
    setGeneratedLink(null);
    setError(null);
    createInvitation({
      variables: {
        organizationSlug: organization.slug,
        role: selectedRole,
      },
    });
  };

  const handleCopy = async () => {
    if (generatedLink) {
      await navigator.clipboard.writeText(generatedLink);
      setShowCopied(true);
      setTimeout(() => setShowCopied(false), 2000);
    }
  };

  const handleDelete = (invitation: StandingInvitation) => {
    if (
      window.confirm(
        'Are you sure you want to revoke this invitation? The link will no longer work.'
      )
    ) {
      deleteInvitation({
        variables: {
          code: invitation.code,
          organizationSlug: organization.slug,
        },
      });
    }
  };

  return (
    <div className="border-t border-gray-200 pt-4 mt-4">
      <h4 className="text-sm font-medium text-gray-700 mb-3">
        Standing Invitations
      </h4>

      {/* Generate new invitation */}
      <div className="mb-4 flex items-center gap-3">
        <select
          value={selectedRole}
          onChange={(e) => setSelectedRole(e.target.value as MembershipRole)}
          className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          <option value={MembershipRole.Admin}>Admin</option>
          <option value={MembershipRole.Member}>Member</option>
        </select>
        <button
          onClick={handleGenerate}
          disabled={createLoading}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 font-medium text-sm"
        >
          {createLoading ? 'Generating...' : 'Generate Invite Link'}
        </button>
      </div>

      {/* Show error */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
          Error: {error}
        </div>
      )}

      {/* Show generated link */}
      {generatedLink && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md">
          <div className="text-sm text-green-800 font-medium mb-2">
            Invitation link created:
          </div>
          <div className="flex items-center gap-2">
            <input
              type="text"
              readOnly
              value={generatedLink}
              className="flex-1 px-3 py-2 bg-white border border-green-300 rounded-md font-mono text-xs"
            />
            <button
              onClick={handleCopy}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 font-medium text-sm whitespace-nowrap"
            >
              {showCopied ? 'Copied!' : 'Copy'}
            </button>
          </div>
        </div>
      )}

      {/* List existing invitations */}
      {organization.standingInvitations.length > 0 && (
        <div>
          <h5 className="text-xs font-medium text-gray-500 uppercase mb-2">
            Active Invitations ({organization.standingInvitations.length})
          </h5>
          <div className="space-y-2">
            {organization.standingInvitations.map((invitation) => (
              <div
                key={invitation.id}
                className="flex items-center justify-between p-2 bg-gray-50 rounded-md text-sm"
              >
                <div className="flex-1">
                  <span className="font-mono text-xs text-gray-600">
                    {invitation.code}
                  </span>
                  <span className="mx-2 text-gray-400">·</span>
                  <span className="text-xs text-gray-500">
                    {invitation.role}
                  </span>
                  <span className="mx-2 text-gray-400">·</span>
                  <span className="text-xs text-gray-500">
                    Created{' '}
                    {formatDistanceToNow(new Date(invitation.createdAt), {
                      addSuffix: true,
                    })}
                  </span>
                </div>
                <button
                  onClick={() => handleDelete(invitation)}
                  disabled={deleteLoading}
                  className="px-2 py-1 text-xs text-red-600 hover:text-red-800 hover:bg-red-50 rounded disabled:opacity-50"
                >
                  Revoke
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
